import { useEffect, useMemo, useState } from 'react';
import { MappedAEMProperties } from '../../../../types';
import { useSession } from '../../../../lib/context/SessionContext';
import Notification, { NotificationType } from '../../base/Notification/Notification';
import { getLastLoginDate, getStoredNotifications, storeNotifications } from './NotificationState';
import styles from './NotificationBox.module.scss';
import { isNil } from '../../../../lib/utils/isNil';

export type AemNotification = {
  id: string;
  content: string;
};

export type NotificationBoxProps = {
  notifications: AemNotification[];
} & MappedAEMProperties;

const NotificationBox = ({ isInEditor, notifications }: NotificationBoxProps) => {
  const [isClosed, setIsClosed] = useState(false);
  const [storedNotifications, setStoredNotifications] = useState(getStoredNotifications());
  const { payload } = useSession();

  const loggedInDate = getLastLoginDate();
  const notification = notifications[0];
  const savedCloseDate = useMemo(() => {
    if (notification === undefined) {
      return null;
    }

    const dateFromStorage = storedNotifications.find(({ id }) => id === notification.id)?.clickedDate;

    return dateFromStorage !== undefined ? new Date(dateFromStorage) : null;
  }, [storedNotifications, notification]);

  useEffect(() => {
    setIsClosed(false);
  }, [payload]);

  useEffect(() => {
    storeNotifications(storedNotifications);
  }, [storedNotifications]);

  const handleClose = () => {
    setIsClosed(() => true);
    setStoredNotifications(items => [
      ...items.filter(item => item.id !== notification.id),
      { id: notification.id, clickedDate: new Date() },
    ]);
  };

  if (isInEditor && notification === undefined) {
    return (
      <Notification
        content="Here will be presented notifications configured in the 'Global Configuration' content fragment"
        onClickHandler={handleClose}
        type={NotificationType.Info}
        wrapperClass={styles.authoringWrapper}
      />
    );
  }

  if (
    isClosed ||
    (loggedInDate && savedCloseDate && loggedInDate.getTime() < savedCloseDate.getTime()) ||
    isNil(notification)
  ) {
    return null;
  }

  return (
    <Notification
      content={notification.content}
      onClickHandler={handleClose}
      type={NotificationType.Info}
      {...(isInEditor && { wrapperClass: styles.authoringWrapper })}
    />
  );
};

export default NotificationBox;
